import React, { Component } from 'react'
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, Input, Button,
} from 'reactstrap'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, convertToRaw, ContentState } from 'draft-js'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import moment from 'moment'
import ModalDatePicker from './ModalDatePicker'
import {
  LEGAL_SUBJECT_OPTIONS,
  LEGAL_PRIORITY_OPTIONS,
  EDITOR_TOOLBAR,
  EDITOR_WRAPPER_STYLE,
  EDITOR_INNER_STYLE,
} from './constants'

const rawToHtml = (editorState) => {
  const raw = convertToRaw(editorState.getCurrentContent())
  return raw.blocks.map(block => block.text.trim() ? `<p>${block.text}</p>` : '').join('')
}

const stripHtml = (html) => {
  if (!html) return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

const htmlToEditorState = (html) => (
  EditorState.createWithContent(ContentState.createFromText(stripHtml(html)))
)

class LegalDemandOptionsModal extends Component {
  constructor(props) {
    super(props)
    this.state = this.emptyForm()
  }

  emptyForm = () => ({
    solicitante: '',
    solicitanteError: '',
    tipo_assunto: 'Outros',
    tipoError: '',
    prioridade: 'Sugestão',
    prioridadeError: '',
    prazo_atendimento: null,
    prazoError: '',
    fato_gerador: '',
    teor: EditorState.createEmpty(),
    teorError: '',
    anexoFile: null,
    fileKey: Date.now(),
  })

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      const d = this.props.defaults || {}
      this.setState({
        ...this.emptyForm(),
        solicitante: d.solicitante || '',
        tipo_assunto: d.tipo_assunto || 'Outros',
        prioridade: d.prioridade || 'Sugestão',
        prazo_atendimento: d.prazo_atendimento || null,
        fato_gerador: d.fato_gerador || '',
        teor: d.teor ? htmlToEditorState(d.teor) : EditorState.createEmpty(),
        anexoFile: d.anexoFile || null,
      })
    }
  }

  handleConfirm = () => {
    const { solicitante, tipo_assunto, prioridade, prazo_atendimento, fato_gerador, teor, anexoFile } = this.state
    let valid = true

    if (!solicitante.trim()) {
      this.setState({ solicitanteError: 'Campo obrigatório.' })
      valid = false
    }
    if (!tipo_assunto) {
      this.setState({ tipoError: 'Campo obrigatório.' })
      valid = false
    }
    if (!prioridade) {
      this.setState({ prioridadeError: 'Campo obrigatório.' })
      valid = false
    }
    if (!prazo_atendimento) {
      this.setState({ prazoError: 'Campo obrigatório.' })
      valid = false
    }
    const teorHtml = rawToHtml(teor).trim()
    if (!teorHtml || teorHtml === '<p></p>') {
      this.setState({ teorError: 'Campo obrigatório.' })
      valid = false
    }

    if (!valid) return

    this.props.onConfirm({
      solicitante: solicitante.trim(),
      tipo_assunto,
      prioridade,
      prazo_atendimento: moment(prazo_atendimento).format('YYYY-MM-DD'),
      fato_gerador: fato_gerador.trim(),
      teor: teorHtml,
      anexoFile: anexoFile || null,
    })
  }

  render() {
    const { isOpen, toggle, contractLabel } = this.props
    const {
      solicitante,
      solicitanteError,
      tipo_assunto,
      tipoError,
      prioridade,
      prioridadeError,
      prazo_atendimento,
      prazoError,
      fato_gerador,
      teor,
      teorError,
      anexoFile,
      fileKey,
    } = this.state

    return (
      <Modal size="lg" scrollable centered enforceFocus={false} isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>Demanda jurídica</ModalHeader>
        <ModalBody className="official-letters-modal">
          <p className="text-muted" style={{ fontSize: 13 }}>
            Configure os dados da solicitação jurídica vinculada a este ofício.
            O teor vem preenchido do ofício, mas pode ser alterado aqui.
          </p>

          <FormGroup>
            <Label>Solicitante</Label>
            <Input
              type="text"
              value={solicitante}
              onChange={e => this.setState({ solicitante: e.target.value, solicitanteError: '' })}
            />
            {solicitanteError && <span style={{ color: 'red', fontSize: 12 }}>{solicitanteError}</span>}
          </FormGroup>

          <FormGroup>
            <Label>Tipo</Label>
            <Input
              type="select"
              value={tipo_assunto}
              onChange={e => this.setState({ tipo_assunto: e.target.value, tipoError: '' })}
            >
              {LEGAL_SUBJECT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Input>
            {tipoError && <span style={{ color: 'red', fontSize: 12 }}>{tipoError}</span>}
          </FormGroup>

          <FormGroup>
            <Label>Prioridade</Label>
            <Input
              type="select"
              value={prioridade}
              onChange={e => this.setState({ prioridade: e.target.value, prioridadeError: '' })}
            >
              {LEGAL_PRIORITY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Input>
            {prioridadeError && <span style={{ color: 'red', fontSize: 12 }}>{prioridadeError}</span>}
          </FormGroup>

          <FormGroup>
            <Label>Prazo de atendimento desejado</Label>
            <ModalDatePicker
              selected={prazo_atendimento}
              onChange={date => this.setState({ prazo_atendimento: date, prazoError: '' })}
            />
            {prazoError && <span style={{ color: 'red', fontSize: 12 }}>{prazoError}</span>}
          </FormGroup>

          <FormGroup>
            <Label>Fato gerador</Label>
            <Input
              type="text"
              value={fato_gerador}
              onChange={e => this.setState({ fato_gerador: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>Teor</Label>
            <div style={{
              ...EDITOR_WRAPPER_STYLE,
              border: teorError ? '1px solid red' : EDITOR_WRAPPER_STYLE.border,
            }}>
              <Editor
                editorState={teor}
                onEditorStateChange={es => this.setState({ teor: es, teorError: '' })}
                toolbar={EDITOR_TOOLBAR}
                toolbarClassName="rdw-toolbar-oficios"
                editorStyle={EDITOR_INNER_STYLE}
                localization={{ locale: 'pt' }}
              />
            </div>
            {teorError && <span style={{ color: 'red', fontSize: 12 }}>{teorError}</span>}
          </FormGroup>

          <FormGroup>
            <Label>Anexar arquivo <span style={{ color: '#888' }}>(opcional)</span></Label>
            <Input
              key={fileKey}
              type="file"
              onChange={e => this.setState({ anexoFile: e.target.files[0] || null })}
            />
            {anexoFile && <span style={{ fontSize: 12, color: '#555' }}>📎 {anexoFile.name}</span>}
          </FormGroup>

          {contractLabel && (
            <FormGroup>
              <Label>Contrato vinculado</Label>
              <Input type="text" value={contractLabel} disabled />
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle}>Cancelar</Button>
          <Button color="primary" onClick={this.handleConfirm}>Confirmar</Button>
        </ModalFooter>
      </Modal>
    )
  }
}

export default LegalDemandOptionsModal
